import { create } from 'zustand'

import { MaxSessionState } from '../types'
import {
  decodeJwtExpiration,
  exchangeAuth0AccessTokenForChatToken,
  fetchMaxProfile,
  resolveMaxTenantFromProfile,
} from '../services/max-session.service'
import { maxDebug } from '../utils/max-debug'

type IdTokenClaims = {
  __raw?: string
}

type InitializeMaxSessionParams = {
  activeProdigyTenantId: string | number
  getIdTokenClaims: () => Promise<IdTokenClaims | undefined>
  force?: boolean
}

type SetLoadingOptions = {
  preserveReady?: boolean
}

type MaxSessionStoreState = {
  session: MaxSessionState
  prodigyTenantId: string | null
  requestId: number
}

type MaxSessionStoreActions = {
  setLoading: (options?: SetLoadingOptions) => void
  setBlocked: (error: string) => void
  clearSession: () => void
  initializeSession: (params: InitializeMaxSessionParams) => Promise<void>
}

type MaxSessionStore = MaxSessionStoreState & MaxSessionStoreActions

export const INITIAL_MAX_SESSION_STATE: MaxSessionState = {
  status: 'idle',
  chatToken: null,
  chatTokenExpiresAt: null,
  maxTenantId: null,
  maxTenantName: null,
  userEmail: null,
  isExploreModeEnabled: false,
  error: null,
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

let requestSequence = 0
let inflightTenantKey: string | null = null
let inflightPromise: Promise<void> | null = null

const useMaxSessionStore = create<MaxSessionStore>((set, get) => ({
  session: INITIAL_MAX_SESSION_STATE,
  prodigyTenantId: null,
  requestId: 0,

  setLoading: (options) => {
    set((state) => {
      if (options?.preserveReady && state.session.status === 'ready') {
        return state
      }

      if (state.session.status === 'loading') {
        return state
      }

      return {
        ...state,
        session: {
          ...INITIAL_MAX_SESSION_STATE,
          status: 'loading',
        },
      }
    })
  },

  setBlocked: (error) => {
    const requestId = ++requestSequence
    set({
      session: {
        ...INITIAL_MAX_SESSION_STATE,
        status: 'blocked',
        error,
      },
      prodigyTenantId: null,
      requestId,
    })
  },

  clearSession: () => {
    const requestId = ++requestSequence
    set({
      session: INITIAL_MAX_SESSION_STATE,
      prodigyTenantId: null,
      requestId,
    })
  },

  initializeSession: async ({ activeProdigyTenantId, getIdTokenClaims, force = false }) => {
    const tenantKey = String(activeProdigyTenantId)
    const currentState = get()

    if (
      !force &&
      currentState.prodigyTenantId === tenantKey &&
      currentState.session.status === 'ready' &&
      currentState.session.chatToken
    ) {
      maxDebug.debug('MAX session bootstrap skipped: cached session reused', {
        prodigyTenantId: tenantKey,
        maxTenantId: currentState.session.maxTenantId,
      })
      return
    }

    if (!force && inflightPromise && inflightTenantKey === tenantKey) {
      return inflightPromise
    }

    const requestId = ++requestSequence
    set({
      session: {
        ...INITIAL_MAX_SESSION_STATE,
        status: 'loading',
      },
      prodigyTenantId: tenantKey,
      requestId,
    })

    inflightTenantKey = tenantKey
    inflightPromise = (async () => {
      try {
        const idClaims = await getIdTokenClaims()
        const authToken = idClaims?.__raw
        if (!authToken) {
          throw new Error('Unable to acquire Auth0 ID token (__raw) for MAX /login exchange.')
        }

        const { chatToken } = await exchangeAuth0AccessTokenForChatToken(authToken)
        const profile = await fetchMaxProfile(chatToken)
        const mappedTenant = resolveMaxTenantFromProfile(profile, tenantKey)

        if (!mappedTenant) {
          const message = `MAX tenant mapping not found for Prodigy tenant ${tenantKey}. Chat is blocked.`
          maxDebug.error(message, {
            activeProdigyTenantId: tenantKey,
            mappedTenants: (profile.user?.availableTenants || []).map((tenant) => ({
              prodigyTenantId: tenant.prodigyTenantId ?? null,
              tenantId: tenant.tenantId ?? null,
              tenantName: tenant.tenantName ?? null,
            })),
          })

          if (get().requestId !== requestId) return

          set({
            session: {
              ...INITIAL_MAX_SESSION_STATE,
              status: 'blocked',
              error: message,
            },
            prodigyTenantId: tenantKey,
            requestId,
          })
          return
        }

        const tokenExpiration = decodeJwtExpiration(chatToken)

        if (get().requestId !== requestId) return

        set({
          session: {
            status: 'ready',
            chatToken,
            chatTokenExpiresAt: tokenExpiration,
            maxTenantId: mappedTenant.maxTenantId,
            maxTenantName: mappedTenant.maxTenantName,
            userEmail: profile.user?.email || null,
            isExploreModeEnabled: mappedTenant.isExploreModeEnabled,
            error: null,
          },
          prodigyTenantId: tenantKey,
          requestId,
        })
      } catch (error) {
        if (get().requestId !== requestId) return

        const message = getErrorMessage(error)
        maxDebug.error('Session init failed', {
          errorName: error instanceof Error ? error.name : 'unknown',
          message,
        })

        set({
          session: {
            ...INITIAL_MAX_SESSION_STATE,
            status: 'error',
            error: message,
          },
          prodigyTenantId: tenantKey,
          requestId,
        })
      } finally {
        if (inflightTenantKey === tenantKey) {
          inflightTenantKey = null
          inflightPromise = null
        }
      }
    })()

    return inflightPromise
  },
}))

export default useMaxSessionStore
